import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Footer } from "@/components/Footer";
import { submitFeedback } from "@/lib/api";

const PRODUCTS = ["OUTLAW", "GHOST", "EXECUTIVE", "TACTICAL", "TITAN", "Future Ideas"];

const RIDING_STYLES = [
  "Commuter",
  "Tourer",
  "Adventure",
  "Cruiser",
  "Sport",
  "Off-road",
  "Track",
  "Everyday",
  "Other",
];

const EXPERIENCE_LEVELS = [
  "New rider (under 2 years)",
  "Comfortable (2–5 years)",
  "Experienced (5–15 years)",
  "Old dog (15+ years)",
];

const initialForm = {
  name: "",
  email: "",
  ride_what: "",
  riding_style: "",
  riding_experience: "",
  product_interest: "",
  love_to_see: "",
  biggest_problem: "",
  other_ideas: "",
};

export default function Feedback() {
  useEffect(() => {
    document.title = "ROADRIG · Help Build the Next ROADRIG";
  }, []);

  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    // At least ONE substantive text field is required.
    const hasSubstance =
      form.love_to_see.trim() || form.biggest_problem.trim() || form.other_ideas.trim();
    if (!hasSubstance) {
      toast.error("Tell us something — a wish, a gripe, or an idea. Even one sentence helps.");
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback(form);
      setSent(true);
      toast.success("Got it. Thanks for helping build ROADRIG.");
      setForm(initialForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="feedback-page">
      {/* Back link */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-8">
        <Link
          to="/"
          data-testid="feedback-back-home"
          className="inline-flex items-center gap-2 serial text-[10px] text-white/40 hover:text-white transition-colors"
        >
          <ArrowLeft size={12} /> BACK TO HOME
        </Link>
      </div>

      {/* Hero */}
      <section className="pt-16 pb-12" data-testid="feedback-hero">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="section-mark" />
            <span className="overline">Rider Feedback Hub</span>
            <span className="section-mark" />
          </div>
          <h1 className="h-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
            Help Build the <span className="text-[#F47A20]">Next ROADRIG</span>.
          </h1>
          <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            {"We're building ROADRIG alongside the riding community. If there's something you've always wanted in a motorcycle bag, or something that drives you crazy about the ones you've owned, tell us. Your idea could end up in the next version."}
          </p>
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-14 border-y border-white/5 bg-white/[0.015]" data-testid="feedback-manifesto">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="overline text-[#F47A20] mb-6">Built Together</div>
          <div className="space-y-5 text-white/70 text-[15px] leading-relaxed">
            <p>
              {"We're not a massive company with a boardroom full of executives. We're just everyday riders building the motorcycle bag we've always wanted but could never find."}
            </p>
            <p>
              {"Some days we get it right. Some days we learn something new. Every step gets us closer. "}
              <strong className="text-white">{"That's why we want you involved."}</strong>
            </p>
            <p>
              {"Some of the best ideas don't come from designers. They come from the bloke who's ridden 500 km in the rain… the woman who commutes every day… the couple touring Australia… the rider who simply says, \"Why don't you do it this way?\""}
            </p>
            <p className="text-white">
              {"We can't promise we'll use every idea. But we can promise we'll read every single one."}
            </p>
            <p className="text-[#F47A20] font-semibold pt-2">
              🏍️ Built by Riders. Designed for the Journey.
            </p>
          </div>
        </div>
      </section>

      {/* Form or thank-you */}
      <section className="py-16 md:py-20" data-testid="feedback-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#F47A20]/40 bg-[#F47A20]/5 rounded-sm p-10 md:p-12 text-center"
              data-testid="feedback-success"
            >
              <CheckCircle2 className="mx-auto text-[#F47A20] mb-5" size={44} />
              <div className="overline text-[#F47A20] mb-4">Feedback received</div>
              <h2 className="h-display text-white text-3xl md:text-4xl mb-4">
                {"Thanks — that's how ROADRIG gets better."}
              </h2>
              <p className="text-white/70 mb-3 max-w-xl mx-auto">
                {"Every submission is read. If we use your idea in a future ROADRIG product, we'll give you a shout-out and a special thank you."}
              </p>
              <p className="text-white/50 text-sm mb-8">
                Got another idea? Fire away.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={() => setSent(false)}
                  className="btn-secondary"
                  data-testid="feedback-submit-another"
                >
                  Send another idea
                </button>
                <Link to="/" className="btn-primary" data-testid="feedback-back-home-btn">
                  Back to ROADRIG
                </Link>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="grid gap-5 md:gap-6" data-testid="feedback-form" noValidate>
              <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                <Field label="Your name (optional)">
                  <input
                    type="text"
                    value={form.name}
                    onChange={on("name")}
                    placeholder="Jane"
                    data-testid="fb-name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email (optional, if you want a shout-out)">
                  <input
                    type="email"
                    value={form.email}
                    onChange={on("email")}
                    placeholder="you@example.com"
                    data-testid="fb-email"
                    className={inputCls}
                  />
                </Field>
              </div>

              <div className="grid md:grid-cols-3 gap-4 md:gap-5">
                <Field label="What do you ride?">
                  <input
                    type="text"
                    value={form.ride_what}
                    onChange={on("ride_what")}
                    placeholder="e.g. Harley Road Glide"
                    data-testid="fb-ride"
                    className={inputCls}
                  />
                </Field>
                <Field label="Riding style">
                  <select
                    value={form.riding_style}
                    onChange={on("riding_style")}
                    data-testid="fb-style"
                    className={selectCls}
                  >
                    <option value="">Choose…</option>
                    {RIDING_STYLES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Riding experience">
                  <select
                    value={form.riding_experience}
                    onChange={on("riding_experience")}
                    data-testid="fb-experience"
                    className={selectCls}
                  >
                    <option value="">Choose…</option>
                    {EXPERIENCE_LEVELS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Which ROADRIG product is your feedback for?">
                <div className="flex flex-wrap gap-2" data-testid="fb-product-group">
                  {PRODUCTS.map((p) => {
                    const active = form.product_interest === p;
                    return (
                      <button
                        type="button"
                        key={p}
                        onClick={() => setForm((f) => ({ ...f, product_interest: active ? "" : p }))}
                        data-testid={`fb-product-${p.toLowerCase().replace(/\s+/g, "-")}`}
                        className={
                          "px-4 py-2 rounded-sm text-[11px] tracking-[0.15em] uppercase font-bold transition-colors border " +
                          (active
                            ? "bg-[#F47A20] border-[#F47A20] text-white"
                            : "border-white/15 text-white/60 hover:text-white hover:border-white/40")
                        }
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="What would you love to see in a rider bag?">
                <textarea
                  rows={4}
                  value={form.love_to_see}
                  onChange={on("love_to_see")}
                  placeholder="A dream feature, a wish-list item, anything…"
                  data-testid="fb-love"
                  className={textareaCls}
                />
              </Field>

              <Field label="What's the biggest problem with your current bag?">
                <textarea
                  rows={4}
                  value={form.biggest_problem}
                  onChange={on("biggest_problem")}
                  placeholder="What breaks, what leaks, what fits wrong, what you hate…"
                  data-testid="fb-problem"
                  className={textareaCls}
                />
              </Field>

              <Field label="Any other ideas?">
                <textarea
                  rows={3}
                  value={form.other_ideas}
                  onChange={on("other_ideas")}
                  placeholder="Anything else — features, colours, materials, mounts…"
                  data-testid="fb-ideas"
                  className={textareaCls}
                />
              </Field>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-3">
                <p className="serial text-[10px] text-white/40 max-w-md leading-relaxed">
                  {"💡 If we use your idea in a future ROADRIG product, we'll give you a shout-out and a special thank you."}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary min-w-[200px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="fb-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Send feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white placeholder:text-white/30 px-4 py-3 rounded-sm transition-colors";
const selectCls =
  "w-full bg-[#0A0A0C] border border-white/15 focus:border-[#F47A20] outline-none text-white px-4 py-3 rounded-sm transition-colors";
const textareaCls =
  "w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white placeholder:text-white/30 px-4 py-3 rounded-sm transition-colors resize-y";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-white/60 mb-2 inline-block">{label}</span>
      {children}
    </label>
  );
}
